/**
 * Utility functions for translation management and availability checking
 */

import React from 'react';

/**
 * Determines if a translation is considered "available" for a given locale
 * 
 * For English locales (en, en_US, en_GB, etc.):
 * - Translation is available if there's a community override OR a default value exists
 * 
 * For other locales:
 * - Translation is available only if there's a community override
 * 
 * @param communityTranslationValue - The value from community_translations table
 * @param defaultValue - The default value from translation_defaults table
 * @param locale - The target locale (e.g., 'en', 'en_GB', 'fr', 'es')
 * @returns true if translation is considered available, false if missing
 */
export const isTranslationAvailable = (
  communityTranslationValue: string | undefined | null,
  defaultValue: string | undefined | null,
  locale: string
): boolean => {
  // Check if there's a community-specific override
  const hasCommunityOverride = communityTranslationValue?.trim() !== '' && communityTranslationValue != null;
  
  // For English locales (en, en_US, en_GB, etc.), we can fall back to defaults
  if (locale.startsWith('en')) {
    const hasDefault = defaultValue?.trim() !== '' && defaultValue != null;
    return hasCommunityOverride || hasDefault;
  }
  
  // For other locales, require explicit community translation
  return hasCommunityOverride;
};

/**
 * Gets the effective translation value for display
 * 
 * @param communityTranslationValue - The value from community_translations table
 * @param defaultValue - The default value from translation_defaults table
 * @param locale - The target locale
 * @returns the effective translation value to display
 */
export const getEffectiveTranslationValue = (
  communityTranslationValue: string | undefined | null,
  defaultValue: string | undefined | null,
  locale: string
): string => {
  // If there's a community override, use it
  if (communityTranslationValue?.trim() !== '' && communityTranslationValue != null) {
    return communityTranslationValue;
  }
  
  // For English locales (en, en_US, en_GB, etc.), fall back to default if no override exists
  if (locale.startsWith('en') && defaultValue?.trim() !== '' && defaultValue != null) {
    return defaultValue;
  }
  
  // No translation available
  return '';
};

/**
 * Checks if a translation key has been modified from its default/current state
 * 
 * @param translationKey - The translation key
 * @param modifiedTranslations - Record of currently modified translations
 * @param currentValue - Current effective value
 * @returns true if the translation has been modified
 */
export const isTranslationModified = (
  translationKey: string,
  modifiedTranslations: Record<string, string>,
  currentValue: string
): boolean => {
  const modifiedValue = modifiedTranslations[translationKey];
  return modifiedValue != null && modifiedValue !== currentValue;
};

/**
 * Options for parsing styled translations
 */
interface ParseStyledTranslationOptions {
  primaryColor?: string;
}

/**
 * Parses a translation string with styled tags and returns React nodes
 * 
 * @param text - The translation text with styled tags (e.g., "<discounts>text</discounts>")
 * @param options - Options for styling (primaryColor, etc.)
 * @returns React nodes with proper styling applied
 */
export const parseStyledTranslation = (
  text: string, 
  options: ParseStyledTranslationOptions = {}
): React.ReactNode => {
  const { primaryColor = '#6b7280' } = options; // Changed to neutral gray instead of orange
  
  if (!text) return null;
  
  // Split by <br/> tags to handle line breaks
  const lines = text.split('<br/>');
  
  return lines.map((line, lineIndex) => {
    if (!line.trim()) return null;
    
    // Use a simpler approach: replace styled tags with React elements
    const processLine = (lineText: string): React.ReactNode[] => {
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      
      // Find all styled tags using regex
      const tagRegex = /<(\w+)>(.*?)<\/\1>/g;
      let match: RegExpExecArray | null;
      
      while ((match = tagRegex.exec(lineText)) !== null) {
        // Add text before the tag
        if (match.index > lastIndex) {
          const beforeText = lineText.slice(lastIndex, match.index);
          if (beforeText) {
            parts.push(beforeText);
          }
        }
        
        // Add the styled content
        const tagName = match[1];
        const content = match[2];
        
        if (tagName === 'discounts') {
          parts.push(
            React.createElement('span', {
              key: `${lineIndex}-${match.index}-${tagName}`,
              style: { color: primaryColor }
            }, content)
          );
        } else {
          // For other tags, just render the content
          parts.push(content);
        }
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add any remaining text after the last tag
      if (lastIndex < lineText.length) {
        const remainingText = lineText.slice(lastIndex);
        if (remainingText) {
          parts.push(remainingText);
        }
      }
      
      // If no tags were found, return the entire line
      if (parts.length === 0) {
        parts.push(lineText);
      }
      
      return parts;
    };
    
    const processedParts = processLine(line);
    
    // Return each line as a React Fragment with proper line breaks
    return React.createElement(React.Fragment, { key: lineIndex }, [
      ...processedParts,
      ...(lineIndex < lines.length - 1 ? [React.createElement('br', { key: `br-${lineIndex}` })] : [])
    ]);
  }).filter(Boolean);
};